// سند هزینه — اعتبارات هزینه‌ای (programKind=0) و تملک دارایی‌های سرمایه‌ای (programKind=1)
import React, { useState, useEffect, useRef } from 'react';
import ListPage, { ui } from '../../components/ListPage';
import { usePagedList } from '../../components/paging';
import { ExcelButton } from '../../components/excel';
import WarningDialog from '../../components/WarningDialog';
import JalaliDatePicker from '../../components/JalaliDatePicker';
import { openReport } from '../../components/report';
import { useFormButtons } from '../../utils/vbPerms';
import { jsonOf, netErr } from '../../utils/http';
import HazinehDocumentDetail from './HazinehDocumentDetail';
import HazinehDocumentReceiveCash from './HazinehDocumentReceiveCash';
import HazinehDocumentAllReports from './HazinehDocumentAllReports';
import HavalehDetailNotes from './HavalehDetailNotes';

const toFa = n => String(n ?? '').replace(/[0-9]/g, d => '۰۱۲۳۴۵۶۷۸۹'[d]);
const toEng = s => String(s ?? '').replace(/[۰-۹]/g, d => '۰۱۲۳۴۵۶۷۸۹'.indexOf(d).toString());
const fmtAmt = n => (Number(n || 0) ? Number(n).toLocaleString('fa-IR') : '•');

const API = (path, opts = {}) => fetch(`/api/tadarokat/hazineh${path}`, {
  headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('access')}` },
  ...opts,
}).then(jsonOf, netErr);

const BUTTONS = ['BTNNEW', 'BTNEDIT', 'BTNDELETE', 'BTNOK', 'BTNCANCEL', 'BTNDETAIL', 'BTNDOCUMENTSUMMARY',
  'BTNHAVALEH', 'BTNHAVALEHDETAIL', 'BTNHAVALEHDETAILNOTES', 'BTNHAZINEHDOCUMENTRECEIVECASH',
  'BTNHAZINEHDOCUMENTREPORT', 'BTNHAZINEHDOCUMENTREPORT2', 'BTNHAZINEHDOCUMENTSUMMARY', 'BTNMOVE',
  'BTNMOVEDOCUMENT', 'BTNPROPERTYALLREPORTS', 'BTNSERVICECONFIRM', 'BTNREMARKPIN', 'BTNREMARKUNPIN'];

const COLS = [
  { key: 'creator', label: 'ثبت کننده' },
  { key: 'UserSerial', label: 'سریال', num: true },
  { key: 'HazinehDocumentNo', label: 'سندهزینه', num: true },
  { key: 'HazinehDocumentDate', label: 'تاریخ' },
  { key: 'Remark', label: 'شرح', wide: true },
  { key: 'cost_centers', label: 'مرکز هزینه' },
  { key: 'notes_count', label: 'یادداشت سند هزینه', cnt: true },
  { key: 'credits', label: 'منبع اعتبار', wide: true },
  { key: 'pay_kind_title', label: 'نوع پرداخت' },
  { key: 'send_eval_title', label: 'ارسال به رسیدگی' },
  { key: 'send_acc_title', label: 'ارسال به تنظیم حساب' },
  { key: 'EvaluationBeginDate', label: 'شروع رسیدگی' },
  { key: 'EvaluationFinishDate', label: 'خاتمه رسیدگی' },
  { key: 'EvaluationReturnDate', label: 'برگشت از رسیدگی' },
  { key: 'SendToBudgetDate', label: 'ارسال به بودجه' },
  { key: 'reject_count', label: 'تعداد واخواهی', cnt: true },
  { key: 'department', label: 'واحد' },
  { key: 'SumPay', label: 'مبلغ', money: true },
  { key: 'AddedTax', label: 'مالیات بر ارزش افزوده', money: true },
  { key: 'AddedInsure', label: 'بیمه سهم دولت', money: true },
  { key: 'DetailDiscount', label: 'تخفیف', money: true },
  { key: 'total_pay', label: 'جمع هزینه', money: true },
  { key: 'Deduction', label: 'کسورات', money: true },
  { key: 'net_pay', label: 'خالص', money: true },
  { key: 'inout_amount', label: 'چکهای صادر شده از حساب تدارکات', money: true },
  { key: 'account', label: 'حساب تدارکات' },
  { key: 'evaluation', label: 'نوع سند' },
  { key: 'letter', label: 'شماره دبیرخانه' },
  { key: 'LetterDate', label: 'تاریخ دبیرخانه' },
  { key: 'DocumentSummaryNo', label: 'نماینده اسناد', num: true },
  { key: 'DocumentSummaryDate', label: 'تاریخ نماینده' },
  { key: 'user_group', label: 'گروه کاربری' },
  { key: 'item_count', label: 'تعداد اقلام', cnt: true },
];

const detailCols = (lv) => [
  { key: 'row_no', label: 'ردیف', num: true }, { key: 'Remark', label: 'شرح', wide: true },
  { key: 'payment_method', label: 'روش پرداخت' }, { key: 'id_no', label: 'کد/شناسه ملی' },
  { key: 'receiver', label: 'فروشنده' }, { key: 'account_no', label: 'حساب' },
  { key: 'sheba_error', label: 'خطای حساب شبا' }, { key: 'bank', label: 'بانک' },
  { key: 'FactorNo', label: 'فاکتور' }, { key: 'FactorDate', label: 'تاریخ' },
  { key: 'Quantity', label: 'تعداد', num: true }, { key: 'UnitPrice', label: 'مبلغ واحد', money: true },
  { key: 'SumPay', label: 'مبلغ', money: true }, { key: 'AddedTax', label: 'مالیات بر ارزش افزوده', money: true },
  { key: 'Discount', label: 'تخفیف', money: true }, { key: 'total', label: 'جمع', money: true },
  { key: 'Deduction', label: 'کسورات', money: true }, { key: 'net', label: 'خالص', money: true },
  { key: 'reject_count', label: 'تعداد واخواهی', cnt: true }, { key: 'buyer', label: 'کارپرداز' },
  { key: 'cost_center', label: 'مرکز هزینه' }, { key: 'credit', label: 'منبع اعتبار' },
  { key: 'program', label: 'برنامه' }, { key: 'project', label: 'پروژه' },
  { key: 'hz0', label: lv[1] }, { key: 'hz1', label: lv[2] }, { key: 'hz2', label: lv[3] },
  { key: 'hz3', label: lv[4] }, { key: 'hz4', label: lv[5] }, { key: 'hz5', label: lv[6] },
  { key: 'shomool', label: 'شمول' }, { key: 'cheque_no', label: 'شماره چک' },
  { key: 'cheque_date', label: 'تاریخ چک' }, { key: 'cheque_amount', label: 'مبلغ چک', money: true },
  { key: 'TaxRefrenceNo', label: 'شماره رهگیری مالیاتی' }, { key: 'TaxRefrenceDate', label: 'تاریخ رهگیری مالیاتی' },
  { key: 'SetadNo', label: 'شماره ستاد' }, { key: 'BillId', label: 'شناسه قبض' }, { key: 'SlipId', label: 'شناسه پرداخت' },
  { key: 'stock_slip_no', label: 'قبض انبار', cnt: true }, { key: 'stock_slip_date', label: 'تاریخ قبض انبار' },
  { key: 'havaleh', label: 'حواله' }, { key: 'havaleh_notes', label: 'یادداشت حواله', cnt: true },
  { key: 'req_with_slip', label: 'درخواست های دارای قبض انبار' },
  { key: 'req_without_slip', label: 'درخواستهای فاقد قبض انبار' },
  { key: 'req_building_repair', label: 'درخواستهای تعمیر ساختمانها' },
  { key: 'req_building_facilities', label: 'درخواستهای منصوبات ساختمانها' },
  { key: 'req_building_insure', label: 'درخواستهای بیمه ساختمانها' },
  { key: 'req_flight', label: 'درخواستهای بلیط هواپیما' },
  { key: 'req_car_repair', label: 'درخواستهای تعمیرات خودرو' },
  { key: 'req_car_fuel', label: 'درخواستهای سوخت خودرو' },
  { key: 'req_car_oil', label: 'درخواستهای روغن خودرو' },
  { key: 'req_car_insure', label: 'درخواستهای بیمه خودرو' },
];

// فیلترهای وضعیت — هر جفت: «انجام شده»، «انجام نشده»
const STATUS = [
  ['ارسال شده', 'ارسال نشده'],
  ['در حال رسیدگی', 'در انتظار رسیدگی'],
  ['برگشت شده', 'برگشت نشده'],
  ['رسیدگی شده', 'رسیدگی نشده'],
  ['ارسال شده به بودجه', 'ارسال نشده به بودجه'],
];

// گزینه‌های «سند هزینه جدید» — مقدار ۰/۱/۲ (۲ = سرجمع)
const PRINT_OPTS = [
  { key: 'cost_center', label: 'مرکز هزینه', three: true, def: 0 },
  { key: 'credit', label: 'منبع اعتبار', three: true, def: 2 },
  { key: 'program', label: 'برنامه/طرح', three: true, def: 2 },
  { key: 'project', label: 'پروژه/فعالیت', three: true, def: 2 },
  { key: 'hazineh', label: 'فصل', three: true, def: 2 },
  { key: 'hazineh1', label: 'زیرفصل/ماده', three: true, def: 0 },
  { key: 'hazineh2', label: 'قسمت/ریزماده', three: true, def: 1 },
  { key: 'hazineh3', label: 'قلم/کدسطح سه', three: true, def: 1 },
  { key: 'amount_receiver', label: 'گیرنده وجه', three: true, def: 0 },
  { key: 'account', label: 'حساب گیرنده وجه', three: true, def: 0 },
  { key: 'shomool', label: 'شمول', three: true, def: 1 },
  { key: 'sum', label: 'تجمیع اقلام حواله', three: true, def: 0 },
  { key: 'remark', label: 'توضیح پرداخت', three: false, def: 0 },
  { key: 'factor', label: 'فاکتور', three: false, def: 0 },
];
const PRINT_KEY = 'tadarokat.hazineh.printopts';

const EMPTY = {
  hazineh_document_no: '', user_serial: '', hazineh_document_date: '', letter_no: '', letter_no2: '',
  letter_date: '', document_summary_no: '', document_summary_date: '', department_id: 0, department: '',
  remark: '', pay_kind: 0, account_code: 0, account: '', evaluation_id: 0, evaluation: '',
  send_to_evaluation: false, send_to_accounting: false,
};

function loadPrintOpts() {
  const d = {};
  PRINT_OPTS.forEach(o => { d[o.key] = o.def; });
  try {
    const s = JSON.parse(localStorage.getItem(PRINT_KEY) || '{}');
    PRINT_OPTS.forEach(o => { if (s[o.key] !== undefined) d[o.key] = Number(s[o.key]) || 0; });
  } catch (e) { /* پیش‌فرض */ }
  return d;
}

const emptyVal = v => v === null || v === undefined || v === '' || v === 0 || v === '0';
const visibleCols = (cols, rows) => cols.filter(c => rows.some(r => !emptyVal(r[c.key])));

function cell(c, v) {
  if (c.money) return fmtAmt(v);
  if (c.cnt) return Number(v || 0) ? toFa(v) : '•';
  return toFa(v);
}

function rowColor(r, coloring, i) {
  let bg = i % 2 ? '#f3e2c7' : '#fff';
  if (!coloring) return bg;
  if (Number(r.EvaluationId || 0) === 0) bg = '#dda0dd';
  if (String(r.EvaluationBeginDate || '').length === 10) bg = '#ff77ff';
  if (String(r.EvaluationFinishDate || '').length === 10) bg = '#9ff';
  if (String(r.SendToBudgetDate || '').length === 10) bg = '#c0c0c0';
  return bg;
}

/** ورودی جستجوی انتخابی: متن + فهرست بازشو + ✕ */
function Lookup({ value, disabled, placeholder, fetcher, onPick, onClear }) {
  const [text, setText] = useState(value || '');
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const timer = useRef(null);
  useEffect(() => { setText(value || ''); }, [value]);
  const search = (q) => {
    setText(q);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      fetcher(q).then(j => { setItems(j && j.ok ? j.data : []); setOpen(true); });
    }, 300);
  };
  return (
    <div style={{ position: 'relative', flex: 1, minWidth: '160px' }}>
      <div style={{ display: 'flex', gap: '4px' }}>
        <input style={ui.input} value={text} disabled={disabled} placeholder={placeholder}
          onChange={e => search(e.target.value)} onFocus={() => !disabled && search(text)}
          onBlur={() => setTimeout(() => setOpen(false), 200)} />
        {!disabled && value && <button type="button" style={ui.btnCancel} onClick={onClear}>✕</button>}
      </div>
      {open && items.length > 0 && (
        <div style={s.drop}>
          {items.map((it, i) => (
            <div key={i} style={s.dropItem} onMouseDown={() => { onPick(it); setOpen(false); }}>{toFa(it.display)}</div>
          ))}
        </div>
      )}
    </div>
  );
}

function Field({ label, children, width }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', width: width || 'auto', flex: width ? 'none' : 1 }}>
      <label style={ui.mlabel}>{label}</label>
      {children}
    </div>
  );
}

function Grid({ cols, rows, selId, idKey, onSelect, onDouble, onContext, coloring }) {
  return (
    <table style={ui.table}>
      <thead><tr>{cols.map(c => <th key={c.key} style={ui.th}>{c.label}</th>)}</tr></thead>
      <tbody>
        {rows.length === 0 && <tr><td colSpan={cols.length || 1} style={ui.empty}>موردی یافت نشد</td></tr>}
        {rows.map((r, i) => (
          <tr key={r[idKey] ?? i} onClick={() => onSelect && onSelect(r)} onDoubleClick={() => onDouble && onDouble(r)}
            onContextMenu={e => { if (onContext) { e.preventDefault(); onContext(r); } }}
            style={{ background: r[idKey] === selId ? '#e8f0fe' : rowColor(r, coloring, i), cursor: 'pointer' }}>
            {cols.map(c => (
              <td key={c.key} style={{ ...(c.money || c.num ? ui.tdNum : ui.td), ...(c.wide ? { whiteSpace: 'normal', minWidth: '200px', textAlign: 'right' } : {}) }}>
                {cell(c, r[c.key])}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Overlay({ children }) {
  return <div style={s.full}>{children}</div>;
}

export default function HazinehDocument({ user, onBack, programKind = 0 }) {
  const kind = Number(programKind) === 1 ? 1 : 0;
  const fb = useFormButtons('FRMHAZINEHDOCUMENT', BUTTONS);
  const fbDetail = useFormButtons('FRMHAZINEHDOCUMENTDETAIL', []);
  const title = kind === 0 ? 'سند هزینه اعتبارات هزینه ای' : 'سند هزینه اعتبارات تملک دارایی های سرمایه ای';

  const [year, setYear] = useState('');
  const [today, setToday] = useState('');
  const [search, setSearch] = useState('');
  const [flags, setFlags] = useState('1111111111');
  const [coloring, setColoring] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [sel, setSel] = useState(null);
  const [details, setDetails] = useState({ rows: [], levels: ['', '', '', '', '', '', ''] });
  const [modal, setModal] = useState(null);            // new | edit
  const [form, setForm] = useState(EMPTY);
  const [onlySend, setOnlySend] = useState(false);
  const [designer, setDesigner] = useState(false);
  const [keepRemark, setKeepRemark] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);                // {lines, title, onOk}
  const [confirm, setConfirm] = useState(null);        // {lines, okLabel, cancelLabel, onOk}
  const [allowOtherYear, setAllowOtherYear] = useState(false);
  const [move, setMove] = useState(null);              // {to_year}
  const [printPanel, setPrintPanel] = useState(false);
  const [printOpts, setPrintOpts] = useState(loadPrintOpts);
  const [rejects, setRejects] = useState(null);
  const [sub, setSub] = useState(null);                // detail | cash | all | notes
  const [toast, setToast] = useState('');

  const L = usePagedList((page, o) => API(`/documents/?page=${page}&program_kind=${kind}&year=${o.year || ''}`
    + `&search=${encodeURIComponent(o.search || '')}&flags=${o.flags || ''}`));

  const reload = (o = {}, focusId = null) => L.reload({ year, search, flags, ...o }).then(j => {
    if (j && j.ok) {
      if (!year) setYear(String(j.year));
      setToday(j.today || '');
      setSel(cur => {
        const id = focusId || (cur && cur.HazinehDocumentId);
        return (j.data || []).find(r => r.HazinehDocumentId === id) || (j.data || [])[0] || null;
      });
    }
    return j;
  });

  useEffect(() => { reload(); }, [kind]);   // بار اول

  useEffect(() => {
    if (!showDetail || !sel) { setDetails(d => ({ ...d, rows: [] })); return; }
    API(`/documents/${sel.HazinehDocumentId}/details/`).then(j => {
      if (j.ok) setDetails({ rows: j.data, levels: j.levels });
    });
  }, [showDetail, sel && sel.HazinehDocumentId]);

  const showToast = m => { setToast(m); setTimeout(() => setToast(''), 3000); };
  const say = (lines) => setMsg({ lines: Array.isArray(lines) ? lines : [lines] });
  const F = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleYear = e => {
    const v = toEng(e.target.value.replace(/[^0-9۰-۹]/g, '')).slice(0, 4);
    setYear(v);
    if (v.length === 4) { setSel(null); reload({ year: v }); }
  };
  const toggleFlag = (i) => {
    const nf = flags.split('').map((c, j) => (j === i ? (c === '1' ? '0' : '1') : c)).join('');
    setFlags(nf); reload({ flags: nf });
  };

  // سال مالی فعال با تاریخ جاری مغایرت دارد
  const yearGuard = (go) => {
    if (allowOtherYear || !today || String(today).slice(0, 4) === String(year)) { go(); return; }
    setConfirm({
      lines: ['سال مالی فعال با تاریخ جاری مغایرت دارد', 'آيا از ادامه کار مطمئن هستيد؟'],
      okLabel: 'بلی', cancelLabel: 'خیر',
      onOk: () => { setAllowOtherYear(true); go(); },
    });
  };

  const openNew = () => yearGuard(async () => {
    const j = await API(`/documents/new-defaults/?year=${year}`);
    if (!j.ok) { say(j.msg); return; }
    setForm({ ...EMPTY, remark: keepRemark && sel ? sel.Remark : '',
      document_summary_no: j.document_summary_no || '', document_summary_date: j.document_summary_date || '',
      hazineh_document_date: j.hazineh_document_date });
    setOnlySend(false); setModal('new');
  });

  const openEdit = () => sel && yearGuard(async () => {
    const j = await API(`/documents/${sel.HazinehDocumentId}/edit-check/`);
    if (!j.ok) { say(j.msg); return; }
    const r = sel;
    setForm({
      hazineh_document_no: r.HazinehDocumentNo, user_serial: r.UserSerial, hazineh_document_date: r.HazinehDocumentDate,
      letter_no: r.LetterNo || '', letter_no2: r.LetterNo2 || '', letter_date: r.LetterDate || '',
      document_summary_no: r.DocumentSummaryNo || '', document_summary_date: r.DocumentSummaryDate || '',
      department_id: r.DepartmentId, department: r.department, remark: r.Remark, pay_kind: r.PayKind || 0,
      account_code: r.AccountCode || 0, account: r.account, evaluation_id: r.EvaluationId || 0, evaluation: r.evaluation,
      send_to_evaluation: !!r.SendToEvaluation, send_to_accounting: !!r.SendToAccounting,
    });
    setDesigner(!!j.designer); setOnlySend(!!j.only_send); setModal('edit');
    if (j.in_evaluation) say(j.msg);
  });

  const save = async () => {
    setSaving(true);
    const body = JSON.stringify({ ...form, year, program_kind: kind });
    const j = modal === 'new'
      ? await API('/documents/create/', { method: 'POST', body })
      : await API(`/documents/${sel.HazinehDocumentId}/update/`, { method: 'PUT', body });
    setSaving(false);
    if (!j.ok) { say(j.lines || j.msg); return; }
    setModal(null);
    if (j.warnings && j.warnings.length) say(j.warnings);
    else showToast('ثبت شد');
    reload({}, j.id);
  };

  const askDelete = () => sel && setConfirm({
    lines: ['آيا از حذف اين رکورد مطمئن هستيد؟'], okLabel: 'بلی', cancelLabel: 'خیر', danger: true,
    onOk: async () => {
      const j = await API(`/documents/${sel.HazinehDocumentId}/delete/`, { method: 'DELETE' });
      if (!j.ok) { say(j.msg); return; }
      setSel(null); showToast('حذف شد'); reload();
    },
  });

  const payKindChange = async (v) => {
    F('pay_kind', v);
    if (v === 0) {
      if (!form.account_code) {
        const j = await API('/documents/last-account/');
        if (j.ok) setForm(f => ({ ...f, account_code: j.id, account: j.display }));
      } else setForm(f => ({ ...f, account_code: 0, account: '' }));
    } else setForm(f => ({ ...f, account_code: 0, account: '' }));
  };

  const summaryNoLeave = async () => {
    if (form.document_summary_date || !form.document_summary_no) return;
    const j = await API(`/documents/summary-date/?year=${year}&no=${toEng(form.document_summary_no)}`);
    if (j.ok) F('document_summary_date', j.document_summary_date);
  };
  const newSummaryNo = async () => {
    const j = await API(`/documents/new-summary-no/?year=${year}`);
    if (j.ok) setForm(f => ({ ...f, document_summary_no: j.document_summary_no, document_summary_date: j.document_summary_date }));
  };

  // انتقال سند به سال دیگر
  const startMove = () => {
    if (!sel) return;
    if (String(sel.EvaluationBeginDate || '').length === 10 && Number(sel.reject_count || 0) === 0) {
      say('این سند در رسیدگی است و امکان انتقال آن وجود ندارد'); return;
    }
    setMove({ to_year: '' });
  };
  const doMove = async () => {
    const ty = toEng(move.to_year);
    const chk = await API(`/documents/${sel.HazinehDocumentId}/move/?to_year=${ty}`);
    if (!chk.ok) { say(chk.msg); return; }
    const run = async () => {
      const j = await API(`/documents/${sel.HazinehDocumentId}/move/`, { method: 'POST', body: JSON.stringify({ to_year: ty }) });
      setMove(null);
      if (!j.ok) { say(j.msg); return; }
      setMsg({ lines: [j.msg], ok: 'متوجه شدم' }); setSel(null); reload();
    };
    const steps = [...(allowOtherYear ? [] : chk.confirms)];
    const next = () => {
      const c = steps.shift();
      if (!c) { run(); return; }
      setConfirm({ lines: c.lines, okLabel: c.confirm, cancelLabel: c.cancel, onOk: next });
    };
    next();
  };

  const report = (key, params) => openReport(key, params).catch(e => say(e.message));
  const docReport = (key, extra = {}) => sel && report(key, { id: sel.HazinehDocumentId, ...extra });
  const documentSummary = () => {
    if (!sel) return;
    if (!Number(sel.DocumentSummaryNo || 0) || String(sel.DocumentSummaryDate || '').length !== 10) {
      say('شماره یا تاریخ نماینده اسناد هزینه نامعتبر است'); return;
    }
    report('tadarokat.documents_summary', { no: sel.DocumentSummaryNo, date: sel.DocumentSummaryDate, program_kind: kind });
  };
  const runPrint = () => {
    try { localStorage.setItem(PRINT_KEY, JSON.stringify(printOpts)); } catch (e) { /* ذخیره نشد */ }
    if (!PRINT_OPTS.some(o => printOpts[o.key]) ) { say('حداقل یک آیتم را برای سطرهای گزارش انتخاب کنید'); return; }
    docReport('tadarokat.hazineh_document2', printOpts);
    setPrintPanel(false);
  };
  const cycleOpt = (o) => setPrintOpts(p => ({ ...p, [o.key]: o.three ? (p[o.key] + 1) % 3 : (p[o.key] ? 0 : 1) }));

  const showRejects = (r) => {
    if (Number(r.reject_count || 0) <= 0) return;
    API(`/documents/${r.HazinehDocumentId}/rejects/`).then(j => { if (j.ok) setRejects(j.data); });
  };

  const canDetail = fb.can('BTNDETAIL') && fbDetail.open;
  const openDetail = (r) => { if (r && canDetail) { setSel(r); setSub('detail'); } };
  const afterSub = () => { setSub(null); reload(); };

  const open = fb.loaded && fb.open;
  const cols = visibleCols(COLS, L.data);
  const dcols = visibleCols(detailCols(details.levels), details.rows)
    .filter(c => !(c.key === 'payment_method' && sel && Number(sel.PayKind) === 1));
  const btn = (style, enabled = true) => ({ ...style, opacity: enabled ? 1 : 0.4, cursor: enabled ? 'pointer' : 'default' });

  const toolbar = open && (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
        <label style={ui.flabel}>سال مالی</label>
        <input value={toFa(year)} onChange={handleYear} maxLength={4} style={ui.yearInput} />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', flex: 1, minWidth: '160px' }}>
        <label style={ui.flabel}>جستجو</label>
        <input value={search} onChange={e => setSearch(e.target.value)} onKeyDown={e => e.key === 'Enter' && reload({ search })}
          style={ui.searchInput} placeholder="شرح، واحد، نوع سند، مبلغ، فاکتور، فروشنده…" />
      </div>
      <div style={s.statusBox}>
        <span style={ui.flabel}>وضعیت رسیدگی</span>
        {STATUS.map((p, i) => (
          <span key={i} style={{ display: 'inline-flex', gap: '6px' }}>
            {p.map((lb, j) => (
              <label key={j} style={s.chk}>
                <input type="checkbox" checked={flags[2 * i + j] === '1'} onChange={() => toggleFlag(2 * i + j)} />{lb}
              </label>
            ))}
          </span>
        ))}
      </div>
      <label style={s.chk}><input type="checkbox" checked={coloring} onChange={e => setColoring(e.target.checked)} />رنگ آمیزی حواله ها با توجه به رسیدگی</label>
      <label style={s.chk}><input type="checkbox" checked={showDetail} onChange={e => setShowDetail(e.target.checked)} />نمایش ریز سند هزینه</label>
      <div style={{ ...ui.toolBtns, flexWrap: 'wrap' }}>
        {fb.can('BTNNEW') && <button style={btn(ui.btnNew)} onClick={openNew}>＋ جدید</button>}
        {fb.can('BTNEDIT') && <button style={btn(ui.btnEdit, !!sel)} disabled={!sel} onClick={openEdit}>✎ ویرایش</button>}
        {fb.can('BTNDELETE') && <button style={btn(ui.btnDel, !!sel)} disabled={!sel} onClick={askDelete}>✕ حذف</button>}
        {canDetail && <button style={btn(ui.btnItems, !!sel)} disabled={!sel} onClick={() => openDetail(sel)}>📋 ریز سند هزینه</button>}
        {fb.can('BTNHAZINEHDOCUMENTRECEIVECASH') && <button style={btn(ui.btnItems)} onClick={() => setSub('cash')}>تنخواه دریافتی</button>}
        {fb.can('BTNHAVALEHDETAILNOTES') && <button style={btn(ui.btnItems, !!sel)} disabled={!sel} onClick={() => setSub('notes')}>یادداشت</button>}
        {fb.can('BTNMOVEDOCUMENT') && <button style={btn(ui.btnEdit, !!sel)} disabled={!sel} onClick={startMove}>انتقال سند</button>}
        {fb.can('BTNPROPERTYALLREPORTS') && <button style={btn(ui.btnItems)} onClick={() => setSub('all')}>گزارش کلی</button>}
        {fb.can('BTNHAVALEH') && <button style={btn(s.rep, !!sel)} disabled={!sel} onClick={() => docReport('tadarokat.hazineh_havaleh')}>حواله</button>}
        {fb.can('BTNHAVALEHDETAIL') && <button style={btn(s.rep, !!sel)} disabled={!sel} onClick={() => docReport('tadarokat.hazineh_havaleh_detail')}>حواله تفصیلی</button>}
        {fb.can('BTNHAZINEHDOCUMENTREPORT') && <button style={btn(s.rep, !!sel)} disabled={!sel} onClick={() => docReport('tadarokat.hazineh_document')}>سند هزینه</button>}
        {fb.can('BTNHAZINEHDOCUMENTREPORT2') && <button style={btn(s.rep, !!sel)} disabled={!sel} onClick={() => setPrintPanel(true)}>سند هزینه جدید</button>}
        {fb.can('BTNHAZINEHDOCUMENTSUMMARY') && <button style={btn(s.rep, !!sel)} disabled={!sel} onClick={() => docReport('tadarokat.hazineh_summary')}>خلاصه سند هزینه</button>}
        {fb.can('BTNDOCUMENTSUMMARY') && <button style={btn(s.rep, !!sel)} disabled={!sel} onClick={documentSummary}>نماینده اسناد</button>}
        {fb.can('BTNSERVICECONFIRM') && <button style={btn(s.rep, !!sel)} disabled={!sel} onClick={() => docReport('tadarokat.service_confirm')}>گواهی سند</button>}
        <ExcelButton title={title} subtitle={`سال مالی ${toFa(year)}`} columns={cols} rows={L.data} />
      </div>
    </>
  );

  const info = sel && (
    <div style={s.info}>
      {[['سند: شماره', sel.HazinehDocumentNo], ['تاریخ', sel.HazinehDocumentDate], ['شماره سریال', sel.UserSerial],
        ['واحد', sel.department], ['شرح هزینه', sel.Remark], ['پرداخت در', sel.pay_kind_title], [' حساب بانکی', sel.account],
        ['نوع سند برای رسیدگی', sel.evaluation], ['دبیرخانه: شماره', sel.letter], ['تاریخ دبیرخانه', sel.LetterDate],
        ['نماینده اسناد: شماره', sel.DocumentSummaryNo], ['تاریخ نماینده', sel.DocumentSummaryDate],
        ['تاریخ رسیدگی: شروع', sel.EvaluationBeginDate], ['خاتمه', sel.EvaluationFinishDate], ['ارسال به بودجه', sel.SendToBudgetDate],
        ['سند شناسایی: شماره', Number(sel.acc_document_no || 0) ? sel.acc_document_no : ''], ['تاریخ سند شناسایی', sel.acc_document_date],
        ['جمع ریز', fmtAmt(sel.total_pay)], ['کسورات', fmtAmt(sel.Deduction)], ['خالص', fmtAmt(sel.net_pay)],
        ['ارسال به رسیدگی', sel.send_eval_title], ['تنظیم حساب', sel.send_acc_title]].map(([l, v]) => (
        <div key={l} style={s.infoItem}><span style={s.infoLabel}>{l}</span><span>{toFa(v)}</span></div>
      ))}
    </div>
  );

  const ro = onlySend;
  return (
    <ListPage user={user} onBack={onBack} title={title} subtitle="تدارکات — سامانه ارجان"
      toolbar={toolbar} error={L.error} pager={open ? L.pager : null} onMore={L.more} loading={L.loading} unit="سند">
      {open && (
        <div style={ui.splitWrap}>
          <div style={ui.topPane}>
            <Grid cols={cols} rows={L.data} selId={sel && sel.HazinehDocumentId} idKey="HazinehDocumentId"
              onSelect={setSel} onDouble={openDetail} onContext={showRejects} coloring={coloring} />
          </div>
          <div style={ui.bottomPane}>
            {info}
            {showDetail && details.rows.length > 0 && (
              <Grid cols={dcols} rows={details.rows} idKey="HazinehDocumentDetailId" />
            )}
          </div>
        </div>
      )}

      {modal && (
        <div style={ui.overlay}>
          <div style={{ ...ui.modal, maxWidth: '860px' }}>
            <div style={ui.mHeader}>
              <span style={ui.mTitle}>{modal === 'new' ? 'سند هزینه جدید' : 'ویرایش سند هزینه'}</span>
              <button style={ui.closeBtn} onClick={() => setModal(null)}>✕</button>
            </div>
            <div style={ui.mBody}>
              <div style={ui.row}>
                <Field label="سند: شماره" width="110px">
                  <input style={ui.input} value={toFa(form.hazineh_document_no)} disabled={!(modal === 'edit' && designer)}
                    onChange={e => F('hazineh_document_no', toEng(e.target.value))} />
                </Field>
                <Field label="شماره سریال" width="110px">
                  <input style={ui.input} value={toFa(form.user_serial)} disabled={!(modal === 'edit' && designer)}
                    onChange={e => F('user_serial', toEng(e.target.value))} />
                </Field>
                <Field label="تاریخ" width="150px">
                  <JalaliDatePicker value={form.hazineh_document_date} onChange={v => F('hazineh_document_date', v)} />
                </Field>
                <Field label="دبیرخانه: شماره">
                  <div style={{ display: 'flex', gap: '4px' }}>
                    <input style={ui.input} value={form.letter_no} disabled={ro} onChange={e => F('letter_no', e.target.value)} />
                    <input style={{ ...ui.input, width: '70px' }} value={toFa(form.letter_no2)} disabled={ro}
                      onChange={e => F('letter_no2', toEng(e.target.value))} />
                  </div>
                </Field>
                <Field label="تاریخ" width="150px">
                  <JalaliDatePicker value={form.letter_date} disabled={ro} onChange={v => F('letter_date', v)} />
                </Field>
              </div>
              <div style={ui.row}>
                <Field label="نماینده اسناد: شماره" width="130px">
                  <input style={ui.input} value={toFa(form.document_summary_no)} disabled={ro}
                    onChange={e => F('document_summary_no', toEng(e.target.value))} onBlur={summaryNoLeave} />
                </Field>
                <Field label="تاریخ" width="150px">
                  <JalaliDatePicker value={form.document_summary_date} disabled={ro} onChange={v => F('document_summary_date', v)} />
                </Field>
                {fb.can('BTNNEW') && !ro && (
                  <button type="button" style={{ ...ui.btnEdit, alignSelf: 'flex-end', cursor: 'pointer' }} onClick={newSummaryNo}>
                    شماره نماینده اسناد جدید</button>
                )}
              </div>
              <div style={ui.row}>
                <Field label="واحد">
                  <Lookup value={form.department} disabled={ro}
                    fetcher={q => API(`/documents/lookup/department/?q=${encodeURIComponent(q)}`)}
                    onPick={it => setForm(f => ({ ...f, department_id: it.id, department: it.title }))}
                    onClear={() => setForm(f => ({ ...f, department_id: 0, department: '' }))} />
                </Field>
              </div>
              <div style={ui.row}>
                <Field label="شرح هزینه">
                  <div style={{ display: 'flex', gap: '4px' }}>
                    <Lookup value={form.remark} disabled={ro}
                      fetcher={q => (q ? API(`/documents/lookup/remark/?q=${encodeURIComponent(q)}`) : Promise.resolve({ ok: true, data: [] }))}
                      onPick={it => F('remark', it.title)} onClear={() => F('remark', '')} />
                    {!ro && (keepRemark
                      ? fb.can('BTNREMARKPIN') && <button type="button" title="شرح برای سند بعدی پاک شود" style={ui.btnCancel} onClick={() => setKeepRemark(false)}>📌</button>
                      : fb.can('BTNREMARKUNPIN') && <button type="button" title="شرح برای سند بعدی بماند" style={ui.btnCancel} onClick={() => setKeepRemark(true)}>📍</button>)}
                  </div>
                </Field>
              </div>
              <div style={ui.row}>
                <Field label="پرداخت در" width="140px">
                  <select style={ui.input} value={form.pay_kind} disabled={ro} onChange={e => payKindChange(Number(e.target.value))}>
                    <option value={0}>تدارکات</option><option value={1}>ذیحسابی</option>
                  </select>
                </Field>
                <Field label=" حساب بانکی">
                  <Lookup value={form.account} disabled={ro}
                    fetcher={q => API(`/documents/lookup/account/?q=${encodeURIComponent(q)}`)}
                    onPick={it => setForm(f => ({ ...f, account_code: it.id, account: it.display }))}
                    onClear={() => setForm(f => ({ ...f, account_code: 0, account: '' }))} />
                </Field>
              </div>
              <div style={ui.row}>
                <Field label="نوع سند برای رسیدگی">
                  <Lookup value={form.evaluation} disabled={ro}
                    fetcher={q => API(`/documents/lookup/evaluation/?q=${encodeURIComponent(q)}`)}
                    onPick={it => setForm(f => ({ ...f, evaluation_id: it.id, evaluation: it.title }))}
                    onClear={() => setForm(f => ({ ...f, evaluation_id: 0, evaluation: '' }))} />
                </Field>
                <label style={{ ...s.chk, alignSelf: 'flex-end' }}>
                  <input type="checkbox" checked={form.send_to_evaluation} disabled={ro} onChange={e => F('send_to_evaluation', e.target.checked)} />ارسال به رسیدگی</label>
                <label style={{ ...s.chk, alignSelf: 'flex-end' }}>
                  <input type="checkbox" checked={form.send_to_accounting} onChange={e => F('send_to_accounting', e.target.checked)} />تنظیم حساب</label>
              </div>
            </div>
            <div style={ui.mFooter}>
              {fb.can('BTNCANCEL') && <button style={ui.btnCancel} onClick={() => setModal(null)}>لغو</button>}
              {fb.can('BTNOK') && fb.can(modal === 'new' ? 'BTNNEW' : 'BTNEDIT') && (
                <button style={ui.btnSave} disabled={saving} onClick={save}>{saving ? '⏳' : 'ثبت'}</button>)}
            </div>
          </div>
        </div>
      )}

      {move && (
        <div style={ui.overlay}>
          <div style={{ ...ui.modal, maxWidth: '360px' }}>
            <div style={ui.mHeader}><span style={ui.mTitle}>انتقال سند</span>
              <button style={ui.closeBtn} onClick={() => setMove(null)}>✕</button></div>
            <div style={ui.mBody}>
              <Field label="به سال">
                <input style={ui.yearInput} maxLength={4} value={toFa(move.to_year)}
                  onChange={e => setMove({ to_year: toEng(e.target.value.replace(/[^0-9۰-۹]/g, '')) })} />
              </Field>
            </div>
            <div style={ui.mFooter}>
              {fb.can('BTNMOVE') && move.to_year.length === 4 && <button style={ui.btnSave} onClick={doMove}>انتقال</button>}
            </div>
          </div>
        </div>
      )}

      {printPanel && (
        <div style={ui.overlay}>
          <div style={{ ...ui.modal, maxWidth: '520px' }}>
            <div style={ui.mHeader}><span style={ui.mTitle}>سند هزینه جدید</span>
              <button style={ui.closeBtn} onClick={() => setPrintPanel(false)}>✕</button></div>
            <div style={{ ...ui.mBody, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
              {PRINT_OPTS.map(o => (
                <label key={o.key} style={s.chk} onClick={e => { e.preventDefault(); cycleOpt(o); }}>
                  <span style={s.tri}>{printOpts[o.key] === 1 ? '✔' : printOpts[o.key] === 2 ? '■' : ''}</span>{o.label}
                </label>
              ))}
              <div style={{ gridColumn: '1 / 3', fontSize: '11px', color: '#777' }}>✔ سطر گزارش — ■ سرجمع</div>
            </div>
            <div style={ui.mFooter}><button style={ui.btnSave} onClick={runPrint}>گزارش</button></div>
          </div>
        </div>
      )}

      {rejects && (
        <div style={ui.overlay} onClick={() => setRejects(null)}>
          <div style={{ ...ui.modal, maxWidth: '560px' }} onClick={e => e.stopPropagation()}>
            <div style={ui.mHeader}><span style={ui.mTitle}>شرح واخواهی</span>
              <button style={ui.closeBtn} onClick={() => setRejects(null)}>✕</button></div>
            <div style={ui.mBody}>
              <Grid cols={[{ key: 'document_no', label: 'شماره سند', num: true }, { key: 'reason', label: 'شرح واخواهی', wide: true },
                { key: 'title', label: 'شرح رسیدگی', wide: true }]} rows={rejects} idKey="reason" />
            </div>
          </div>
        </div>
      )}

      {sub === 'detail' && sel && (
        <HazinehDocumentDetail user={user} documentId={sel.HazinehDocumentId} programKind={kind}
          sendToEvaluation={!!sel.SendToEvaluation} amount={sel.total_pay}
          onClose={afterSub} onChanged={() => reload()} />
      )}
      {sub === 'cash' && <Overlay><HazinehDocumentReceiveCash user={user} programKind={kind} year={year} onClose={() => setSub(null)} /></Overlay>}
      {sub === 'all' && (
        <Overlay><HazinehDocumentAllReports user={user} programKind={kind} year={year} onClose={() => setSub(null)}
          onOpenDocument={canDetail ? (id) => { setSel(s2 => ({ ...(s2 || {}), HazinehDocumentId: id })); setSub('detail'); } : null} /></Overlay>
      )}
      {sub === 'notes' && sel && (
        <HavalehDetailNotes user={user} hazinehDocumentId={sel.HazinehDocumentId}
          title={`یادداشتهای سند هزینه ${toFa(sel.HazinehDocumentNo)}`} onClose={afterSub} />
      )}

      {msg && (
        <WarningDialog title="پیام" lines={msg.lines} confirmLabel={msg.ok || 'تایید'} cancelLabel="بستن"
          onConfirm={() => setMsg(null)} onCancel={() => setMsg(null)} />
      )}
      {confirm && (
        <WarningDialog title="هشدار" lines={confirm.lines} confirmLabel={confirm.okLabel} cancelLabel={confirm.cancelLabel}
          tone={confirm.danger ? 'danger' : 'warn'}
          onConfirm={() => { const c = confirm; setConfirm(null); c.onOk(); }} onCancel={() => setConfirm(null)} />
      )}
      {toast && <div style={ui.toast}>{toast}</div>}
    </ListPage>
  );
}

const s = {
  drop: { position: 'absolute', top: '100%', right: 0, left: 0, background: '#fff', border: '1px solid #ddd', borderRadius: '8px', maxHeight: '240px', overflowY: 'auto', zIndex: 1100, boxShadow: '0 6px 18px rgba(0,0,0,0.15)' },
  dropItem: { padding: '7px 10px', fontSize: '12px', cursor: 'pointer', borderBottom: '1px solid #f3f3f3' },
  chk: { display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '12px', color: '#333', cursor: 'pointer', whiteSpace: 'nowrap' },
  statusBox: { display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'center', background: '#f7f8fb', padding: '6px 10px', borderRadius: '8px' },
  rep: { padding: '9px 14px', background: 'linear-gradient(135deg,#0a1628,#1a2f50)', color: '#C8A84B', border: 'none', borderRadius: '8px', fontFamily: 'inherit', fontSize: '12.5px', fontWeight: '700' },
  info: { display: 'flex', flexWrap: 'wrap', gap: '6px 18px', padding: '10px 14px', fontSize: '12px' },
  infoItem: { display: 'flex', gap: '6px', alignItems: 'baseline' },
  infoLabel: { color: '#777', fontSize: '11px' },
  tri: { display: 'inline-flex', width: '16px', height: '16px', border: '1.5px solid #1a2f50', borderRadius: '3px', alignItems: 'center', justifyContent: 'center', fontSize: '11px', color: '#1a2f50' },
  full: { position: 'fixed', inset: 0, zIndex: 900, background: '#f0f2f7' },
};
